import type { PicksAction } from '@/lib/picks/picksActionTypes'
import {
  buildPicksFallbackForMissingCallPhone,
  getResolvedCallPhoneForTel,
} from '@/lib/picks/callActionPhone'
import { isPicksDebugEnabled } from '@/lib/picks/picksDebug'

/** Same gate as picksCallDiagnosticsEnabled — NEXT_PUBLIC_PICKS_DEBUG=true. */
export function picksCtaTraceEnabled(): boolean {
  return isPicksDebugEnabled()
}

/**
 * Trace for clicks that go through runPicksUserAction → executePicksAction.
 * `incoming` is the action passed into the runner; `effective` is what executePicksAction receives.
 */
export function logPicksCtaTraceForRun(
  incoming: PicksAction,
  effective: PicksAction,
  meta: {
    component: string
    label?: string | null
    hasAnchorWrapper?: boolean
    hasHref?: boolean
  }
): void {
  if (!picksCtaTraceEnabled()) return

  const resolvedIncoming =
    incoming.type === 'call'
      ? getResolvedCallPhoneForTel(incoming) || null
      : null
  const resolvedEffective =
    effective.type === 'call'
      ? getResolvedCallPhoneForTel(effective) || null
      : null

  const fallbackType =
    incoming.type === 'call' && !resolvedIncoming
      ? buildPicksFallbackForMissingCallPhone(incoming).type
      : null

  const finalExecutor =
    effective.type === 'call' && resolvedEffective
      ? 'executePicksAction:tel'
      : `executePicksAction:${effective.type}`

  console.debug('[PICKS CTA TRACE]', {
    component: meta.component,
    label: meta.label ?? null,
    actionType: incoming.type,
    actionTypeExecuted: effective.type,
    placeName: incoming.place?.name ?? incoming.title ?? null,
    resolvedPhone: resolvedIncoming,
    hasCallablePhone: incoming.type === 'call' && Boolean(resolvedIncoming),
    usesRunPicksUserAction: true,
    hasAnchorWrapper: meta.hasAnchorWrapper ?? false,
    hasHref: meta.hasHref ?? false,
    fallbackActionType: fallbackType,
    finalExecutor,
    preventDefaultLikelyNeeded: false,
    stopPropagationLikelyNeeded: false,
  })
}

/** Clicks that bypass the Picks action runner (raw window.open, Uber shell, etc.). */
export function logPicksCtaTraceBypass(opts: {
  component: string
  label?: string | null
  action?: PicksAction | null
  mechanism: string
}): void {
  if (!picksCtaTraceEnabled()) return
  const a = opts.action
  console.debug('[PICKS CTA TRACE]', {
    component: opts.component,
    label: opts.label ?? null,
    actionType: a?.type ?? null,
    actionTypeExecuted: null,
    placeName: a?.place?.name ?? a?.title ?? null,
    resolvedPhone:
      a?.type === 'call' ? getResolvedCallPhoneForTel(a) || null : null,
    hasCallablePhone:
      a?.type === 'call' ? Boolean(getResolvedCallPhoneForTel(a)) : false,
    usesRunPicksUserAction: false,
    hasAnchorWrapper: false,
    hasHref: false,
    fallbackActionType: null,
    finalExecutor: opts.mechanism,
    preventDefaultLikelyNeeded: false,
    stopPropagationLikelyNeeded: false,
  })
}
